import { FC, memo, useCallback } from 'react';
import { Form, Input, message, Modal, Typography } from 'antd';
import { useUIDispatchContext, useModalStateContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_PASSWORD } from 'gql/profile/mutations';
import { UpdatePasswordInput } from '__generated__/globalTypes';

import { showErrorMessage } from 'utils/showErrorMessage';

const { Title } = Typography;

const PasswordModal: FC = () => {
  // translations
  const { t } = useTranslation();
  // context
  const { password } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  // form
  const [form] = Form.useForm();

  const closeModal = () => {
    toggleModal('password', false);
    form.resetFields();
  };

  const [updatePassword, { loading: isUpdatingPassword }] = useMutation(
    UPDATE_PASSWORD,
    {
      onCompleted() {
        message.success(t('messages.successfully_updated'));
        closeModal();
      },
      onError: err => showErrorMessage(err)
    }
  );

  const onPasswordFinish = useCallback(
    (values: UpdatePasswordInput) => {
      updatePassword({
        variables: {
          input: {
            current_password: values.current_password,
            password: values.password
          }
        }
      });
    },
    [updatePassword]
  );

  return (
    <Modal
      open={password.visible}
      confirmLoading={isUpdatingPassword}
      onCancel={closeModal}
      onOk={form.submit}
    >
      <Title level={4}>{t('profile.change_password')}</Title>
      <Form
        form={form}
        onFinish={onPasswordFinish}
        layout="vertical"
        size="large"
      >
        <Form.Item
          rules={[{ required: true }]}
          name="current_password"
          label={t('profile.password')}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
        <Form.Item
          rules={[{ required: true }]}
          name="password"
          label={t('profile.new_password')}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label={t('profile.confirm_password')}
          rules={[
            {
              required: true
            },
            {
              message: t('profile.passwords_not_match'),
              validator: (_, value) => {
                if (value === form.getFieldValue('password')) {
                  return Promise.resolve();
                }

                return Promise.reject();
              }
            }
          ]}
        >
          <Input type="password" maxLength={255} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(PasswordModal);
